function toAmount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

const LABELS = {
  tr: {
    reportTitle: 'Ozkaynak Degisim Tablosu',
    statementName: 'IFRS Ozkaynak Degisim Tablosu',
    lineItem: 'Hareket Kalemi',
    currentPeriod: 'Cari Donem',
    previousPeriod: 'Onceki Donem',
    noComparative: 'Karsilastirmali onceki donem verisi bulunamadi.',
    basisNote: 'Sunum, onceki onayli batch acilis bakiyesi ile cari batch kapanis bakiyesi arasindaki ozkaynak hareketlerini matris formatinda gosterir.',
    totalColumn: 'Toplam Ozkaynak',
    opening_balance: 'Acilis Bakiyesi',
    profit_for_period: 'Donem Kari / Zarari',
    oci_movement: 'Diger Kapsamli Gelir',
    total_comprehensive_income: 'Toplam Kapsamli Gelir',
    other_changes: 'Diger Ozkaynak Degisimleri',
    closing_balance: 'Kapanis Bakiyesi'
  },
  en: {
    reportTitle: 'Statement of Changes in Equity',
    statementName: 'IFRS Statement of Changes in Equity',
    lineItem: 'Movement Line Item',
    currentPeriod: 'Current Period',
    previousPeriod: 'Previous Period',
    noComparative: 'No comparative previous-period data was found.',
    basisNote: 'Presentation shows equity movements in matrix format between the opening balance from the previous approved batch and the current closing balance.',
    totalColumn: 'Total Equity',
    opening_balance: 'Opening Balance',
    profit_for_period: 'Profit / Loss for the Period',
    oci_movement: 'Other Comprehensive Income',
    total_comprehensive_income: 'Total Comprehensive Income',
    other_changes: 'Other Changes in Equity',
    closing_balance: 'Closing Balance'
  }
};

const COLUMN_LABELS_EN = {
  SHARE_CAPITAL: 'Share Capital',
  '500': 'Share Capital',
  CAPITAL_RESERVES: 'Capital Reserves',
  PROFIT_RESERVES: 'Profit Reserves',
  RETAINED_EARNINGS: 'Retained Earnings',
  ACCUMULATED_LOSSES: 'Accumulated Losses',
  PERIOD_PROFIT_LOSS: 'Profit or Loss for the Period',
  BALANCING_RESERVE: 'Balancing Reserve',
  TOTAL_EQUITY: 'Total Equity'
};

const TURKISH_COLUMN_LABELS_EN = {
  'odenmis sermaye': 'Share Capital',
  'sermaye yedekleri': 'Capital Reserves',
  'kar yedekleri': 'Profit Reserves',
  'gecmis yil karlari': 'Retained Earnings',
  'gecmis yillar karlari': 'Retained Earnings',
  'gecmis yil zararlari': 'Accumulated Losses',
  'gecmis yillar zararlari': 'Accumulated Losses',
  'donem net kar zarari': 'Profit or Loss for the Period',
  'donem net kari zarari': 'Profit or Loss for the Period',
  'donem kari zarari': 'Profit or Loss for the Period',
  'dengeleme rezervi': 'Balancing Reserve',
  'toplam ozkaynak': 'Total Equity'
};

function normalizeLabel(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u0131/g, 'i')
    .replace(/\u0130/g, 'I')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function stripEquityPrefix(key) {
  return String(key || '').replace(/^EQUITY_/i, '').trim();
}

function mapColumnKeyToEnglish(key) {
  const cleanKey = stripEquityPrefix(key).toUpperCase();
  if (COLUMN_LABELS_EN[cleanKey]) {
    return COLUMN_LABELS_EN[cleanKey];
  }
  if (cleanKey.startsWith('590') || cleanKey.startsWith('591')) {
    return COLUMN_LABELS_EN.PERIOD_PROFIT_LOSS;
  }
  if (cleanKey.startsWith('58')) {
    return COLUMN_LABELS_EN.ACCUMULATED_LOSSES;
  }
  if (cleanKey.startsWith('57')) {
    return COLUMN_LABELS_EN.RETAINED_EARNINGS;
  }
  if (cleanKey.startsWith('54') || cleanKey.startsWith('55')) {
    return COLUMN_LABELS_EN.PROFIT_RESERVES;
  }
  if (cleanKey.startsWith('52')) {
    return COLUMN_LABELS_EN.CAPITAL_RESERVES;
  }
  if (cleanKey.startsWith('50') || cleanKey.startsWith('51')) {
    return COLUMN_LABELS_EN.SHARE_CAPITAL;
  }
  return '';
}

function mapRowLabel(row, labels) {
  return labels[row?.key] || row?.label || row?.key || '-';
}

function mapColumnLabel(column, labels, isTr) {
  if (column?.key === 'TOTAL_EQUITY') {
    return labels.totalColumn;
  }
  if (!isTr) {
    const keyLabel = mapColumnKeyToEnglish(column?.key);
    if (keyLabel) {
      return keyLabel;
    }
    const labelKey = normalizeLabel(column?.label);
    if (TURKISH_COLUMN_LABELS_EN[labelKey]) {
      return TURKISH_COLUMN_LABELS_EN[labelKey];
    }
  }
  return column?.label || column?.key || '-';
}

function mapRowVariant(rowType) {
  if (rowType === 'summary') return 'summary-strong';
  if (rowType === 'total') return 'total';
  if (rowType === 'opening') return 'summary';
  return 'line';
}

export function buildEquityChangesPresentation({
  currentReport,
  previousReport,
  currentPeriodLabel,
  previousPeriodLabel,
  isTr
}) {
  const labels = isTr ? LABELS.tr : LABELS.en;
  const locale = isTr ? 'tr-TR' : 'en-US';
  const columns = Array.isArray(currentReport?.matrixColumns) ? currentReport.matrixColumns : [];
  const rows = Array.isArray(currentReport?.matrixRows) ? currentReport.matrixRows : [];

  return {
    viewType: 'matrix',
    labels,
    locale,
    currentPeriodLabel: currentPeriodLabel || labels.currentPeriod,
    previousPeriodLabel: previousPeriodLabel || labels.previousPeriod,
    comparativeAvailable: Boolean(previousReport?.matrixRows?.length || previousReport?.lines?.length),
    metadataNote: labels.basisNote,
    columns: columns.map((column) => ({
      key: column.key,
      label: mapColumnLabel(column, labels, isTr),
      category: column.category || '',
      openingBalance: toAmount(column.openingBalance),
      closingBalance: toAmount(column.closingBalance)
    })),
    rows: rows.map((row) => ({
      key: row.key,
      label: mapRowLabel(row, labels),
      variant: mapRowVariant(row.rowType),
      values: row.values || {}
    }))
  };
}
